/**
 * Haptic Feedback Service
 * Provides tactile feedback for user interactions on supported devices
 * Primarily designed for mobile devices with vibration API support
 */

interface HapticSettings {
  enabled: boolean;
  intensity: 'light' | 'medium' | 'heavy';
}

// Haptic patterns for different interaction types
export const HapticPatterns = {
  // Light feedback
  TAP: 10,                    // Quick tap (button press)
  TICK: 5,                    // Subtle tick (checkbox, switch)
  
  // Medium feedback
  CLICK: 20,                  // Standard click
  SELECT: 25,                 // Item selection
  TOGGLE: [10, 50, 10],       // Toggle on/off
  
  // Heavy feedback
  SUCCESS: [30, 50, 30],      // Success action
  ERROR: [50, 100, 50, 100, 50], // Error/warning
  IMPACT: 40,                 // Heavy impact
  
  // Notification patterns
  NOTIFICATION: [20, 100, 20], // Incoming notification
  LEVEL_UP: [30, 100, 30, 100, 30, 100, 30], // Achievement/level up
  QUEST_COMPLETE: [20, 50, 20, 50, 20], // Quest completion
  
  // Navigation
  SWIPE: 15,                  // Swipe gesture
  PAGE_CHANGE: [10, 30, 10],  // Page navigation
  
  // Long press
  LONG_PRESS_START: [10, 100], // Long press detected
  LONG_PRESS_END: 30,          // Long press released
} as const;

class HapticService {
  private settings: HapticSettings = {
    enabled: true,
    intensity: 'medium',
  };
  
  private isSupported: boolean = false;
  private lastHapticTime: number = 0;
  private minHapticInterval: number = 50; // Minimum ms between haptics to avoid overwhelming

  constructor() {
    if (typeof window !== 'undefined') {
      this.checkSupport();
      this.loadSettings();
    }
  }

  /**
   * Check if haptic feedback is supported on this device
   */
  private checkSupport(): void {
    this.isSupported = 
      'vibrate' in navigator || 
      'mozVibrate' in navigator || 
      'webkitVibrate' in navigator;
  }

  /**
   * Load settings from localStorage
   */
  private loadSettings(): void {
    try {
      const saved = localStorage.getItem('hapticSettings');
      if (saved) {
        const parsed = JSON.parse(saved);
        this.settings = { ...this.settings, ...parsed };
      }
    } catch (error) {
      console.warn('Failed to load haptic settings:', error);
    }
  }

  /**
   * Save settings to localStorage
   */
  private saveSettings(): void {
    try {
      localStorage.setItem('hapticSettings', JSON.stringify(this.settings));
    } catch (error) {
      console.warn('Failed to save haptic settings:', error);
    }
  }

  /**
   * Apply intensity multiplier to pattern
   */
  private applyIntensity(pattern: number | number[]): number | number[] {
    const multipliers = {
      light: 0.6,
      medium: 1.0,
      heavy: 1.4,
    };
    
    const multiplier = multipliers[this.settings.intensity];
    
    if (typeof pattern === 'number') {
      return Math.round(pattern * multiplier);
    }
    
    return pattern.map(val => Math.round(val * multiplier));
  }

  /**
   * Throttle haptic feedback to prevent overwhelming the user
   */
  private shouldTriggerHaptic(): boolean {
    const now = Date.now();
    if (now - this.lastHapticTime < this.minHapticInterval) {
      return false;
    }
    this.lastHapticTime = now;
    return true;
  }

  /**
   * Trigger haptic feedback with a pattern
   */
  private vibrate(pattern: number | number[]): void {
    if (!this.isSupported || !this.settings.enabled) {
      return;
    }

    if (!this.shouldTriggerHaptic()) {
      return;
    }

    try {
      const adjustedPattern = this.applyIntensity(pattern);
      
      // Try standard API first
      if ('vibrate' in navigator) {
        navigator.vibrate(adjustedPattern);
      } 
      // Fallback to vendor-prefixed versions
      else if ('mozVibrate' in navigator) {
        (navigator as any).mozVibrate(adjustedPattern);
      } else if ('webkitVibrate' in navigator) {
        (navigator as any).webkitVibrate(adjustedPattern);
      }
    } catch (error) {
      // Silently fail - haptics are enhancement only
      console.debug('Haptic feedback failed:', error);
    }
  }

  // ============================================
  // PUBLIC API - Interaction-specific methods
  // ============================================

  /**
   * Light tap feedback - for buttons, links
   */
  tap(): void {
    this.vibrate(HapticPatterns.TAP);
  }

  /**
   * Subtle tick - for checkboxes, switches
   */
  tick(): void {
    this.vibrate(HapticPatterns.TICK);
  }

  /**
   * Standard click - for primary actions
   */
  click(): void {
    this.vibrate(HapticPatterns.CLICK);
  }

  /**
   * Selection feedback - for selecting items
   */
  select(): void {
    this.vibrate(HapticPatterns.SELECT);
  }

  /**
   * Toggle feedback - for switches, toggles
   */
  toggle(): void {
    this.vibrate([...HapticPatterns.TOGGLE]);
  }

  /**
   * Success feedback - for successful actions
   */
  success(): void {
    this.vibrate([...HapticPatterns.SUCCESS]);
  }

  /**
   * Error feedback - for errors, warnings
   */
  error(): void {
    this.vibrate([...HapticPatterns.ERROR]);
  }

  /**
   * Impact feedback - for heavy interactions
   */
  impact(): void {
    this.vibrate(HapticPatterns.IMPACT);
  }

  /**
   * Notification feedback - for incoming notifications
   */
  notification(): void {
    this.vibrate([...HapticPatterns.NOTIFICATION]);
  }

  /**
   * Level up feedback - for achievements, level ups
   */
  levelUp(): void {
    this.vibrate([...HapticPatterns.LEVEL_UP]);
  }

  /**
   * Quest complete feedback - for quest completion
   */
  questComplete(): void {
    this.vibrate([...HapticPatterns.QUEST_COMPLETE]);
  }

  /**
   * Swipe feedback - for swipe gestures
   */
  swipe(): void {
    this.vibrate(HapticPatterns.SWIPE);
  }

  /**
   * Page change feedback - for navigation
   */
  pageChange(): void {
    this.vibrate([...HapticPatterns.PAGE_CHANGE]);
  }

  /**
   * Long press start feedback
   */
  longPressStart(): void {
    this.vibrate([...HapticPatterns.LONG_PRESS_START]);
  }

  /**
   * Long press end feedback
   */
  longPressEnd(): void {
    this.vibrate(HapticPatterns.LONG_PRESS_END);
  }

  /**
   * Custom pattern - for special cases
   */
  custom(pattern: number | number[]): void {
    this.vibrate(pattern);
  }

  // ============================================
  // SETTINGS API
  // ============================================

  /**
   * Enable or disable haptic feedback
   */
  setEnabled(enabled: boolean): void {
    this.settings.enabled = enabled;
    this.saveSettings();
  }

  /**
   * Get current enabled state
   */
  isEnabled(): boolean {
    return this.settings.enabled;
  }

  /**
   * Set haptic intensity
   */
  setIntensity(intensity: 'light' | 'medium' | 'heavy'): void {
    this.settings.intensity = intensity;
    this.saveSettings();
  }

  /**
   * Get current intensity
   */
  getIntensity(): 'light' | 'medium' | 'heavy' {
    return this.settings.intensity;
  }

  /**
   * Get current settings
   */
  getSettings(): HapticSettings {
    return { ...this.settings };
  }

  /**
   * Update settings
   */
  updateSettings(settings: Partial<HapticSettings>): void {
    this.settings = { ...this.settings, ...settings };
    this.saveSettings();
  }

  /**
   * Check if device supports haptic feedback
   */
  isHapticSupported(): boolean {
    return this.isSupported;
  }

  /**
   * Test haptic feedback with current settings
   */
  test(): void {
    if (!this.isSupported) {
      console.warn('Haptic feedback is not supported on this device');
      return;
    }
    
    // Test with a pattern
    this.vibrate([30, 50, 30, 50, 30]);
  }

  /**
   * Cancel any ongoing vibration
   */
  cancel(): void {
    if (!this.isSupported) {
      return;
    }

    try {
      if ('vibrate' in navigator) {
        navigator.vibrate(0);
      } else if ('mozVibrate' in navigator) {
        (navigator as any).mozVibrate(0);
      } else if ('webkitVibrate' in navigator) {
        (navigator as any).webkitVibrate(0);
      }
    } catch (error) {
      console.debug('Failed to cancel haptic:', error);
    }
  }
}

// Export singleton instance
export const hapticService = new HapticService();

// Export for use in React components
export default hapticService;

